import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { createTheme, CssBaseline, ThemeProvider } from '@mui/material';
import App from './App';
import store from './redux/store/Store';
import './index.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/open-sans/400.css';
import '@fontsource/open-sans/700.css';
import './i18n/config';
import { colors } from './constants/constants';

const root = ReactDOM.createRoot(document.getElementById('root'));
const mdTheme = createTheme({
  palette: {
    primary: {
      main: '#2EAF98',
      contrastText: '#FFF',
    },
    secondary: {
      main: '#201547',
      contrastText: '#FFF',
    },
    warning: {
      main: '#EF6C00',
    },
    controlChip: {
      main: 'rgba(32, 21, 71, 0.3)',
      contrastText: '#000',
    },
    steps: {
      main: '#6a6a6a',
      light: '#BDBDBD',
      dark: '#2EAF98',
    },
    soaLink: {
      main: '#2EAF98',
    },
    background: {
      default: colors.white,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {},
    },
    MuiAccordion: {
      variants: [
        {
          props: { variant: 'listed' },
          style: {
            backgroundColor: colors.white,
            borderRadius: 3,
          },
        },
      ],
    },
    MuiAvatar: {
      variants: [
        {
          props: { variant: 'responsiblePerson' },
          style: {
            height: 35,
            width: 50,
          },
        },
        {
          props: { variant: 'login' },
          style: {
            height: 64,
            width: 64,
            backgroundColor: colors.mainGreen,
          },
        },
      ],
    },
    MuiButtonGroup: {
      variants: [
        {
          props: { variant: 'outlined', subtype: 'buttonGroupSelect' },
          style: {
            minWidth: 'max-content',
          },
        },
      ],
    },
    MuiButton: {
      variants: [
        {
          props: { variant: 'contained', subtype: 'auditee' },
          style: {
            maxWidth: '250px',
          },
        },
        {
          props: { variant: 'outlined', subtype: 'buttonGroupSelect' },
          style: {
            maxWidth: 'max-content',
            minWidth: 'fit-content',
          },
        },
        {
          props: { variant: 'contained', subtype: 'buttonGroupSelect' },
          style: {
            maxWidth: 'max-content',
            minWidth: 'fit-content',
          },
        },
        {
          props: { variant: 'contained', subtype: 'soa' },
          style: {
            maxWidth: '365px',
          },
        },
        {
          props: { variant: 'evaluationListText' },
          style: {
            border: 'none',
            textTransform: 'none',
          },
        },
        {
          props: { variant: 'no-hover' },
          style: {
            margin: 3,
            color: colors.black,
            ':hover': { backgroundColor: 'transparent' },
          },
        },
        {
          props: { variant: 'outlined' },
          style: {
            borderRadius: 3,
            width: 'max-content',
            ':hover': { borderColor: colors.buttonBorder },
          },
        },
      ],
    },
    MuiButtonBase: {
      variants: [
        {
          props: { variant: 'controlLink' },
          style: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: 'white',
            padding: '15px',
            borderRadius: '5px',
          },
        },
      ],
    },
    MuiCheckbox: {
      variants: [
        {
          props: { variant: 'checkbox' },
          style: {
            color: colors.wireButton,
            '&.Mui-checked': {
              color: colors.wireButton,
            },
          },
        },
      ],
    },
    MuiCard: {
      variants: [
        {
          props: { variant: 'controlDetails' },
          style: {
            background: 'transparent',
          },
        },
        {
          props: { variant: 'elevation', subtype: 'controls' },
          style: {
            margin: 2,
            paddingTop: '8px',
            paddingRight: '16px',
            paddingBottom: '8px',
            paddingLeft: '16px',
            backgroundColor: colors.white,
          },
        },
      ],
    },
    MuiCardContent: {
      variants: [
        {
          props: { variant: 'controlDetailsContent' },
          style: {
            padding: '0px',
            '&:last-child': {
              paddingBottom: '0px',
            },
          },
        },
      ],
    },
    MuiChip: {
      variants: [
        {
          props: { variant: 'activityStatus' },
          style: {
            borderRadius: '15px',
            border: '1px solid',
            borderColor: colors.chipGrey,
            backgroundColor: colors.chipGrey,
            width: '120px',
          },
        },
        {
          props: { variant: 'outlined', subtype: 'evalChip' },
          style: {
            margin: '3px',
          },
        },
        {
          props: { variant: 'filled', subtype: 'evalChip' },
          style: {
            margin: '3px',
            backgroundColor: colors.wireButton,
            ':hover': { backgroundColor: colors.wireButton },
          },
        },
        {
          props: { variant: 'filled', subtype: 'info' },
          style: {
            backgroundColor: colors.info,
          },
        },
        {
          props: { variant: 'filled', subtype: 'minor' },
          style: {
            backgroundColor: colors.minor,
          },
        },
        {
          props: { variant: 'filled', subtype: 'major' },
          style: {
            backgroundColor: colors.major,
          },
        },
        {
          props: { variant: 'filled', subtype: 'majorFindings' },
          style: {
            backgroundColor: colors.major,
          },
        },
        {
          props: { variant: 'filled', subtype: 'minorFindings' },
          style: {
            backgroundColor: colors.minor,
          },
        },
        {
          props: { variant: 'filled', subtype: 'infoFindings' },
          style: {
            backgroundColor: colors.info,
          },
        },
        {
          props: { variant: 'filled', subtype: 'noFindings' },
          style: {
            backgroundColor: colors.compliant,
          },
        },
        {
          props: { variant: 'inLine' },
          style: {
            borderRadius: '100px',
          },
        },
        {
          props: { variant: 'controlChip' },
          style: {
            height: '24px',
            paddingTop: '2px',
          },
        },
      ],
    },
    MuiContainer: {
      variants: [
        {
          props: { variant: 'mainContentArea' },
          style: {
            height: '100vh',
            minHeight: 0,
          },
        },
        {
          props: { variant: 'activityItem' },
          style: {
            borderRadius: '10px',
            border: '1px solid',
            backgroundColor: 'white',
            boxShadow: 5,
            padding: '15px',
            margin: '10px',
          },
        },
        {
          props: { variant: 'baseView' },
          style: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            padding: '24px 24px 0 24px',
          },
        },
        {
          props: { variant: 'baseView', subtype: 'activityManagement' },
          style: {
            padding: '24px',
          },
        },

        {
          props: { variant: 'assessmentDataView' },
          style: {
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            marginBottom: '32px',
            paddingBottom: '2px',
          },
        },
        {
          props: { variant: 'evaluationContentArea' },
          style: {
            minHeight: 0,
            flex: 1,
          },
        },
        {
          props: { variant: 'assessmentDataViewFixed' },
          style: {
            display: 'flex',
            flexDirection: 'row',
            height: '100%',
            marginTop: '16px',
            paddingBottom: '32px',
            minHeight: 0,
            flex: 1,
          },
        },
        {
          props: { variant: 'assessmentList' },
          style: {
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            maxHeight: '35vh',
          },
        },
        {
          props: { variant: 'buttonContainer' },
          style: {
            display: 'flex',
            justifyContent: 'flex-end',
          },
        },
        {
          props: { variant: 'buttonContainer', subtype: 'left' },
          style: {
            display: 'flex',
            justifyContent: 'flex-start',
          },
        },
        {
          props: { variant: 'buttonRow' },
          style: {
            padding: '8px',
          },
        },
        {
          props: { variant: 'chipContainer' },
          style: {
            padding: '5px',
          },
        },
        {
          props: { variant: 'controlBaseView' },
          style: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
          },
        },
        {
          props: { variant: 'controlDataView' },
          style: {
            display: 'flex',
            flexDirection: 'row',
            height: '100%',
            marginTop: '8px',
            minHeight: 0,
            flex: 1,
          },
        },
        {
          props: { variant: 'evaluation' },
          style: {
            display: 'flex',
            flexDirection: 'row',
            p: 0,
            m: 0,
          },
        },
        {
          props: { variant: 'evaluationList' },
          style: {
            padding: 5,
            flexGrow: 1,
            maxHeight: '20vh',
          },
        },
        {
          props: { variant: 'expandedFinding' },
          style: {
            borderRadius: '10px',
            border: '1px solid',
            backgroundColor: 'white',
            boxShadow: 5,
            paddingTop: '10px',
            paddingBottom: '10px',
          },
        },
        {
          props: { variant: 'findingList' },
          style: {
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            maxHeight: '100vh',
            paddingTop: '10px',
            marginTop: '5px',
            paddingBottom: '5px',
            marginBottom: '15px',
          },
        },
        {
          props: { variant: 'formContainer' },
          style: {
            maxHeight: '80vh',
            overflowY: 'auto',
            paddingTop: '16px',
            paddingRight: '24px',
            paddingBottom: '16px',
            paddingLeft: '24px',
          },
        },
        {
          props: { variant: 'innerContainer' },
          style: {
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '16px',
          },
        },
        {
          props: { variant: 'innerContainer', subtype: 'indented' },
          style: {
            paddingLeft: '16px',
            paddingRight: '16px',
          },
        },
        {
          props: { variant: 'modal' },
          style: {
            display: 'flex',
            flexDirection: 'column',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '50%',
            maxHeight: '95%',
            minHeight: 'max-content',
            transform: 'translate(-50%, -50%)',
            backgroundColor: colors.white,
          },
        },
        {
          props: { variant: 'organizationList' },
          style: {
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            maxHeight: '80vh',
            paddingBottom: '5px',
            marginBottom: '15px',
            padding: '24px',
          },
        },
        {
          props: { variant: 'review' },
          style: {
            paddingTop: '10px',
            paddingBottom: '10px',
            alignContent: 'start',
          },
        },
        {
          props: { variant: 'reviewDetailContainer' },
          style: {
            paddingTop: '10px',
            paddingBottom: '10px',
            alignContent: 'start',
          },
        },
        {
          props: { variant: 'selectedControls' },
          style: {
            flexGrow: 3,
          },
        },
        {
          props: { variant: 'settingsModal' },
          style: {
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '50%',
            maxHeight: '95%',
            minHeight: 'max-content',
            transform: 'translate(-50%, -50%)',
            backgroundColor: colors.white,
          },
        },
      ],
    },
    MuiList: {
      variants: [
        {
          props: { variant: 'evaluationList' },
          style: {
            paddingRight: 4,
            paddingLeft: 4,
          },
        },
        {
          props: { variant: 'evaluationSubList' },
          style: {
            padding: 5,
            maxWidth: '100%',
          },
        },
        {
          props: { variant: 'controlList' },
          style: {
            paddingLeft: 16,
            paddingRight: 16,
            paddingBottom: 8,
          },
        },
      ],
    },
    MuiListItem: {
      variants: [
        {
          props: { variant: 'selected' },
          style: {
            backgroundColor: 'colors.selected',
          },
        },
      ],
    },
    MuiRadio: {
      variants: [
        {
          props: { variant: 'radio' },
          style: {
            '&.Mui-checked': {
              color: colors.wireButton,
            },
          },
        },
      ],
    },
    MuiSelect: {
      variants: [
        {
          props: { variant: 'outlined' },
          style: {
            padding: 0,
            margin: 0,
          },
        },
      ],
    },
    MuiStack: {
      variants: [
        {
          props: { variant: 'homeSectionCard' },
          style: {
            backgroundColor: colors.white,
            paddingTop: 24,
            paddingBottom: 24,
            paddingLeft: 20,
            paddingRight: 20,
          },
        },
        {
          props: { variant: 'activityList' },
          style: {
            backgroundColor: colors.white,
            height: 75,
            padding: 5,
            margin: 5,
            display: 'flex',
            alignItems: 'flex-start',
          },
        },
        {
          props: { variant: 'assessLinkRow' },
          style: {
            alignItems: 'center',
          },
        },
        {
          props: { variant: 'assessmentData' },
          style: {
            maxWidth: '1200px',
            minWidth: '900px',
          },
        },
        {
          props: { variant: 'assessmentDetails' },
          style: {
            paddingTop: '8px',
            paddingRight: '16px',
            paddingBottom: '8px',
            paddingLeft: '16px',
            alignItems: 'center',
            justifyContent: 'space-between',
          },
        },
        {
          props: { variant: 'assessmentHeader' },
          style: {
            alignItems: 'center',
            gap: '14px',
          },
        },

        {
          props: { variant: 'assessmentPhases' },
          style: {
            maxWidth: '1200px',
            paddingTop: '8px',
            paddingBottom: '16px',
          },
        },
        {
          props: { variant: 'assessmentSelectionHeader' },
          style: {
            alignItems: 'center',
            justifyContent: 'flex-start',
            marginTop: '24px',
            marginBottom: '32px',
          },
        },
        {
          props: { variant: 'documentField' },
          style: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          },
        },
        {
          props: { variant: 'elevated' },
          style: {
            padding: '4px',
          },
        },
        {
          props: { variant: 'expandedFinding' },
          style: {
            paddingTop: '10px',
            paddingBottom: '10px',
            width: '45%',
          },
        },
        {
          props: { variant: 'filter' },
          style: {
            gap: '10px',
          },
        },
        {
          props: { variant: 'frameworkButtonRow' },
          style: {
            alignItems: 'center',
            justifyContent: 'space-between',
          },
        },
        {
          props: { variant: 'headerRow' },
          style: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            marginTop: '24px',
            marginBottom: '40px',
            border: true,
          },
        },
        {
          props: { variant: 'poweredSection' },
          style: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginBottom: 32,
          },
        },
        {
          props: { variant: 'itemRow' },
          style: {
            height: 50,
            padding: 5,
            margin: 12,
            alignItems: 'center',
            justifyContent: 'space-between',
          },
        },
        {
          props: { variant: 'login' },
          style: {
            gap: '18px',
          },
        },
        {
          props: { variant: 'modalButtons' },
          style: {
            padding: '8px',
            justifyContent: 'flex-end',
          },
        },
        {
          props: { variant: 'modalTop' },
          style: {
            marginTop: '16px',
            marginRight: '24px',
            marginBottom: '20px',
            marginLeft: '24px',
            overflowY: 'auto',
          },
        },
        {
          props: { variant: 'organizationInfo' },
          style: {
            width: '50%',
          },
        },
        {
          props: { variant: 'responsible' },
          style: {
            alignItems: 'center',
            gap: '5px',
          },
        },
        {
          props: { variant: 'scopingRow' },
          style: {
            alignItems: 'center',
            justifyContent: 'space-between',
          },
        },
        {
          props: { variant: 'soa' },
          style: {
            paddingRight: 2,
            paddingLeft: 2,
            gap: '24px',
          },
        },
        {
          props: { variant: 'applicableTitle' },
          style: {
            flexDirection: 'row',
            justifyContent: 'flex-start',
            minWidth: '120px',
            alignItems: 'center',
            marginRight: '8px',
            marginLeft: '32px',
          },
        },
        {
          props: { variant: 'incompleteChip' },
          style: {
            flexDirection: 'row',
            justifyContent: 'flex-start',
            minWidth: '110px',
            alignItems: 'center',
            marginRight: '8px',
            marginLeft: '32px',
          },
        },
        {
          props: { variant: 'controlIcons' },
          style: {
            flexDirection: 'row',
            alignItems: 'center',
            marginLeft: '16px',
          },
        },
      ],
    },
    MuiPaper: {
      variants: [
        {
          props: { variant: 'elevation', subtype: 'activityCompletion' },
          style: {
            padding: 10,
            backgroundColor: colors.stackBgGrey,
          },
        },
        {
          props: { variant: 'elevation', subtype: 'completion' },
          style: {
            padding: 8,
            backgroundColor: colors.completionBg,
          },
        },
        {
          props: { variant: 'elevation', subtype: 'assessmentField' },
          style: {
            margin: 8,
            backgroundColor: colors.white,
          },
        },
        {
          props: { variant: 'elevation', subtype: 'finding' },
          style: {
            padding: 8,
            paddingRight: 16,
            paddingLeft: 16,
            backgroundColor: colors.white,
          },
        },
        {
          props: { variant: 'elevation', subtype: 'frameworkField' },
          style: {
            padding: 16,
            backgroundColor: colors.white,
          },
        },
        {
          props: { variant: 'elevation', subtype: 'reportField' },
          style: {
            padding: 10,
            margin: 8,
            backgroundColor: colors.white,
          },
        },
        {
          props: { variant: 'elevation', subtype: 'documentField' },
          style: {
            backgroundColor: colors.white,
          },
        },
        {
          props: { variant: 'elevation', subtype: 'organizationInfo' },
          style: {
            paddingLeft: 5,
            paddingRight: 5,
            paddingTop: 0,
            paddingBottom: 5,
            backgroundColor: colors.stackBgGrey,
          },
        },
        {
          props: { variant: 'elevation', subtype: 'reportControls' },
          style: {
            marginTop: 10,
            marginBottom: 10,
            padding: 5,
          },
        },
      ],
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: colors.wireButton,
        },
      },
    },
    MuiTab: {
      variants: [
        {
          props: { variant: 'tab' },
          style: {
            '&.Mui-selected': {
              color: colors.wireButton,
            },
          },
        },
      ],
    },
    MuiTextField: {
      variants: [
        {
          props: { variant: 'outlined', subtype: 'documentField' },
          style: {
            backgroundColor: colors.white,
          },
        },
        {
          props: { variant: 'outlined', subtype: 'signinField' },
          style: {
            backgroundColor: colors.white,
            '& label.Mui-focused': {
              color: colors.mainGreen,
            },
            '& .MuiInput-underline:after': {
              borderBottomColor: colors.mainGreen,
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: colors.mainGreen,
              },
              '&:hover fieldset': {
                borderColor: colors.mainGreen,
              },
              '&.Mui-focused fieldset': {
                borderColor: colors.mainGreen,
              },
            },
          },
        },
      ],
    },
  },
  MuiStep: {
    variants: [
      {
        props: { variant: 'assessment' },
        style: {
          padding: 5,
          '&.Mui-active': {
            '&.MuiStepIcon-root': {
              color: colors.wireBg,
            },
          },
        },
      },
    ],
  },
  MuiStepConnector: {
    variants: [
      {
        props: { variant: 'assessment' },
        style: {
          borderColor: colors.wireBg,
        },
      },
    ],
  },
  MuiStepper: {
    variants: [
      {
        props: { variant: 'assessment' },
        style: {
          padding: 5,
          marginTop: '16px',
          marginBottom: '16px',
        },
      },
    ],
  },
  typography: {
    body1: {
      fontFamily: 'Roboto',
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '24px',
      letterSpacing: '0.15px',
      textAlign: 'left',
    },
    body2: {
      fontFamily: 'Roboto',
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20.02px',
      letterSpacing: '0.17px',
    },
    bold: {
      fontFamily: 'Roboto',
      fontSize: '16px',
      fontWeight: 'bold',
      color: colors.black,
    },
    button: {
      lineHeight: '0px',
    },
    buttonMedium: {
      fontFamily: 'Roboto',
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '24px',
      letterSpacing: '0.4px',
    },
    buttonSmall: {
      fontFamily: 'Roboto',
      fontSize: '13px',
      fontWeight: 500,
      lineHeight: '22px',
      letterSpacing: '0.46px',
    },
    buttonDivider: {
      fontFamily: 'Roboto',
      fontSize: '14px',
    },
    chip: {
      fontFamily: 'Roboto',
      fontSize: '13px',
      fontWeight: 400,
      textAlign: 'center',
      lineHeight: '18px',
      letterSpacing: '0.16px',
    },
    error: {
      fontFamily: 'Roboto',
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '24px',
      letterSpacing: '0.5px',
      textAlign: 'left',
      color: colors.error,
    },
    overline: {
      fontFamily: 'Roboto',
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '31.92px',
      letterSpacing: '1px',
    },
    notification: {
      fontFamily: 'Roboto',
      fontSize: '18px',
      fontWeight: 'bold',
      lineHeight: '24px',
      textAlign: 'left',
      color: colors.error,
    },
    header: {
      fontFamily: 'Roboto',
      fontSize: '24px',
      fontWeight: 700,
      lineHeight: '28px',
      textAlign: 'left',
      color: '#2EAF98',
    },
    tabHeader: {
      fontFamily: 'Open Sans',
      fontSize: '24px',
      fontWeight: 700,
      lineHeight: '32.68px',
      color: '#2EAF98',
    },
    italics: {
      fontFamily: 'Roboto',
      fontStyle: 'italic',
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '22px',
      color: colors.black,
    },
    light: {
      fontFamily: 'Roboto',
      fontSize: '16px',
      fontWeight: 'thin',
      color: colors.grey,
    },
    listH1: {
      fontFamily: 'Roboto',
      fontSize: '20px',
      fontWeight: 700,
      lineHeight: '23.44px',
    },
    listH2: {
      fontFamily: 'Roboto',
      fontSize: '18px',
      fontWeight: 700,
      lineHeight: '21.09px',
    },
    listH3: {
      fontFamily: 'Roboto',
      fontSize: '18px',
      fontWeight: 400,
      lineHeight: '21.09px',
    },
    organizationName: {
      fontFamily: 'Roboto',
      fontSize: '32px',
      fontWeight: 600,
      lineHeight: '32px',
      letterSpacing: '0.25px',
      textAlign: 'center',
    },
    organization: {
      fontFamily: 'Roboto',
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: '20px',
      letterSpacing: '0.15px',
      textTransform: 'lowercase',
    },
    regular: {
      fontFamily: 'Roboto',
      fontSize: '16px',
      fontWeight: 400,
      color: colors.black,
    },
    reviewDetails: {
      minHeight: '150px',
    },
    settingsDesc: {
      fontFamily: 'Roboto',
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '18.75px',
    },
    viewDesc: {
      fontFamily: 'Roboto',
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '18.75px',
    },
    soaVersion: {
      fontFamily: 'Roboto',
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: '21.09px',
    },
    subHeader: {
      fontFamily: 'Roboto',
      fontSize: '32px',
      fontWeight: 600,
      textAlign: 'left',
      lineHeight: '28.13px',
      color: colors.wireButton,
    },
    title: {
      fontFamily: 'Open sans',
      fontSize: '32px',
      fontWeight: 700,
      textAlign: 'left',
      lineHeight: '43.58px',
      color: colors.headerColor,
    },
    underlined: {
      textDecoration: 'underline',
    },
    h6: {
      fontFamily: 'Roboto',
      fontSize: '20px',
      fontWeight: 500,
      lineHeight: '32px',
      letterSpacing: '0.15px',
    },
    controlList0: {
      fontFamily: 'Roboto',
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: '30px',
      letterSpacing: '0.15px',
    },
    controlList1: {
      fontFamily: 'Roboto',
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: '27px',
      letterSpacing: '0.15px',
    },
    controlList2: {
      fontFamily: 'Roboto',
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '24px',
      letterSpacing: '0.15px',
    },
    documentLink: {
      fontFamily: 'Roboto',
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '24px',
      letterSpacing: '0.15px',
      textAlign: 'left',
      color: colors.mainGreen,
      ':hover': { color: colors.headerColor },
    },
    drawer: {
      color: colors.white,
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '0.15px',
      ':hover': { color: colors.mainGreen },
    },
    drawerButton: {
      color: colors.white,
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '24px',
      letterSpacing: '0.4px',
    },
    drawerButtonOutlined: {
      color: colors.mainGreen,
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '24px',
      letterSpacing: '0.4px',
    },
    drawerOrgHeader: {
      fontFamily: 'Roboto',
      fontWeight: 600,
      fontSize: '24px',
      lineHeight: '32.02px',
    },
    drawerOrgSub: {
      fontFamily: 'Roboto',
      fontWeight: 600,
      fontSize: '20px',
      lineHeight: '20px',
      letterSpacing: '0.15px',
      textTransform: 'lowercase',
    },
    drawerAdmin: {
      fontFamily: 'Roboto',
      fontWeight: 600,
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '0.15px',
    },
    loginHeader: {
      fontFamily: 'Roboto',
      fontWeight: 600,
      fontSize: '88px',
      lineHeight: '78px',
      letterSpacing: '0.15px',
      color: colors.white,
      textTransform: 'uppercase',
    },
    loginText: {
      fontFamily: 'Roboto',
      fontWeight: 600,
      fontSize: '20px',
      lineHeight: '30px',
      letterSpacing: '0.15px',
      color: colors.white,
    },
    signinText: {
      fontFamily: 'Roboto',
      fontWeight: 400,
      fontSize: '24px',
      lineHeight: '32px',
    },
    signinButton: {
      fontFamily: 'Roboto',
      fontWeight: 500,
      fontSize: '15px',
      lineHeight: '26px',
      letterSpacing: '0.46px',
    },
    noActivities: {
      fontFamily: 'Open sans',
      fontWeight: 600,
      fontSize: '20px',
      lineHeight: '27.24px',
      color: colors.noEntitiesText,
    },
    noDocuments: {
      fontFamily: 'Roboto',
      fontWeight: 400,
      fontSize: '16px',
      color: colors.noEntitiesText,
    },
    findingsSubheader: {
      fontFamily: 'Roboto',
      fontWeight: 500,
      fontSize: '16px',
      lineHeight: '32px',
      letterSpacing: '1px',
      color: colors.mainGreen,
      textTransform: 'uppercase',
    },
    soaApplicability: {
      fontFamily: 'Roboto',
      fontWeight: 400,
      fontSize: '11px',
      lineHeight: '24px',
      letterSpacing: '0.5px',
    },
    passwordValidators: {
      fontFamily: 'Roboto',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '18px',
      letterSpacing: '0.5px',
    },
    soaLink: {
      fontFamily: 'Roboto',
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '0.15px',
      color: colors.mainGreen,
    },
    soaOwner: {
      fontFamily: 'Roboto',
      fontWeight: 700,
      fontSize: '14px',
      lineHeight: '21px',
      letterSpacing: '0.15px',
    },
    soaReportControlTitle: {
      fontFamily: 'Open sans',
      fontWeight: 600,
      fontSize: '16px',
      lineHeight: '18.77px',
    },
    soaSubTitle: {
      fontFamily: 'Open sans',
      fontWeight: 700,
      fontSize: '20px',
      lineHeight: '28.15px',
      color: colors.mainGreen,
    },
    soaTitle: {
      fontFamily: 'Open sans',
      fontWeight: 700,
      fontSize: '24px',
      lineHeight: '28px',
      color: colors.mainGreen,
    },
    soaIncompleteTitle: {
      fontFamily: 'Open sans',
      fontWeight: 700,
      fontSize: '20px',
      lineHeight: '28px',
    },
    homeSectionTitle: {
      fontFamily: 'Open sans',
      fontWeight: 700,
      fontSize: '28px',
      lineHeight: '32px',
      color: colors.mainGreen,
    },
    homeSubtitle: {
      fontFamily: 'Open sans',
      fontWeight: 700,
      fontSize: '20px',
      lineHeight: '27px',
      color: colors.mainGreen,
    },
    activityTitle: {
      fontFamily: 'Roboto',
      fontWeight: 500,
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '0.15px',
    },
    controlTitle: {
      fontFamily: 'Roboto',
      fontWeight: 500,
      fontSize: '16px',
      lineHeight: '18.75px',
    },
  },
});
root.render(
  <ThemeProvider theme={mdTheme}>
    <CssBaseline />
    <BrowserRouter>
      <Provider store={store}>
        <App />
      </Provider>
    </BrowserRouter>
  </ThemeProvider>,
);
